import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Typography
} from '@material-ui/core';
import React, { FC, memo, useCallback } from 'react';
import styled from 'styled-components';
import { viewTexts } from '../../../constants/view-texts';
import {
  MuiCheckboxWithLabel,
  MuiInput,
  MuiPasswordInput
} from '../../shared/form-element';

const vt = viewTexts.dialogs.editUser;

const InputWrapper = styled.div`
  padding: 5px;
`;

interface Props {
  open: boolean;
  onClose: (reason: 'ok' | 'cancel' | '') => void;
  username: string | undefined;
  usernameErrors: string[] | undefined;
  usernameOnInput: (v: string) => void;
  email: string | undefined;
  emailErrors: string[] | undefined;
  emailOnInput: (v: string) => void;
  password: string | undefined;
  passwordErrors: string[] | undefined;
  passwordOnInput: (v: string) => void;
  isActive: boolean;
  onActiveFlagChange: (b: boolean) => void;
  submitButtonEnabled: boolean;
  nonFieldErrors: string[];
}

export const EditUserDialogView: FC<Props> = memo(
  ({
    open,
    onClose,
    username = '',
    usernameErrors = [],
    usernameOnInput,
    email = '',
    emailErrors = [],
    emailOnInput,
    password = '',
    passwordErrors = [],
    passwordOnInput,
    isActive,
    onActiveFlagChange,
    submitButtonEnabled,
    nonFieldErrors
  }: Readonly<Props>) => {
    const onCloseByEscape = useCallback(() => {
      onClose('');
    }, [onClose]);

    const cancelOnClick = useCallback(() => {
      onClose('cancel');
    }, [onClose]);

    const okOnClick = useCallback(() => {
      onClose('ok');
    }, [onClose]);

    return (
      <Dialog open={open} onClose={onCloseByEscape}>
        <DialogTitle>{vt.title}</DialogTitle>
        <DialogContent>
          <Typography>{vt.description1}</Typography>
          <Typography>{vt.description2}</Typography>
          <InputWrapper>
            <MuiInput
              label={vt.label1}
              required={true}
              value={username}
              onInput={usernameOnInput}
              helperText={usernameErrors.join(' ')}
              error={usernameErrors.length > 0}
            />
          </InputWrapper>
          <InputWrapper>
            <MuiInput
              label={vt.label2}
              type='email'
              required={true}
              value={email}
              onInput={emailOnInput}
              helperText={emailErrors.join(' ')}
              error={emailErrors.length > 0}
            />
          </InputWrapper>
          <InputWrapper>
            <MuiPasswordInput
              label={vt.label3}
              required={false}
              value={password}
              onInput={passwordOnInput}
              helperText={passwordErrors.join(' ')}
              error={passwordErrors.length > 0}
            />
            <FormHelperText error={false}>
              {password.length === 0 ? vt.inputHint : ''}
            </FormHelperText>
          </InputWrapper>
          <InputWrapper>
            <MuiCheckboxWithLabel
              label={vt.userIsActive}
              checked={isActive}
              onChange={onActiveFlagChange}
            />
          </InputWrapper>
          {nonFieldErrors.map((er, i) => (
            <FormHelperText key={i} error={true}>
              {er}
            </FormHelperText>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelOnClick} color='default'>
            {vt.cancel}
          </Button>
          <Button
            disabled={!submitButtonEnabled}
            onClick={okOnClick}
            color='primary'
            autoFocus={true}
          >
            {vt.ok}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

EditUserDialogView.displayName = 'EditUserDialogView';
